/**
 * Capillary's internal design-system color tokens (from the Cap UI Library
 * Storybook: Foundations / Color tokens / Palette). These are NOT tenant brand
 * colors — they're used as sensible defaults in buildAntdTheme.ts for slots the
 * per-tenant branding JSON (public/theme/{tenantID}.json) doesn't define, so gaps
 * fall back to Capillary's own palette instead of antd's generic stock colors.
 * Tenant-specific values always take priority over these when present.
 */

export const CAP_PRIMARY = {
  base: '#47af46',
  disabled: '#a1d8a0',
  hover: '#1f9a1d',
};

export const CAP_SECONDARY = {
  base: '#2466ea',
  light: 'rgba(36, 102, 234, 0.1)',
};

export const CAP_SEMANTIC = {
  error: '#dc2626',
  info: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
};

export const CAP_GREY = {
  G01: '#091e42',
  G02: '#253858',
  G03: '#42526e',
  G04: '#5e6c84',
  G05: '#97a0af',
  G06: '#b3bac5',
  G07: '#dfe2e7',
  G08: '#ebecf0',
  G09: '#f4f5f7',
  G10: '#fafbfc',
};

export const CAP_NEUTRAL = {
  black: '#000000',
  white: '#ffffff',
};

export const CAP_FONT_COLOR = {
  primary: '#091e42', // FONT_COLOR_01
  secondary: '#5e6c84', // FONT_COLOR_02
  disabled: '#97a0af', // FONT_COLOR_03
  link: '#2466ea', // FONT_COLOR_05
  onDark: '#ffffff', // FONT_COLOR_06
};

// Additional accent colors requested for use alongside the base palette above:
// an orange highlight/accent, and a pale green used as a soft "success" background
// (e.g. behind success tags/badges/buttons) rather than a solid fill.
export const CAP_ACCENT = {
  orange: 'rgb(249, 115, 22)',
  successBackground: '#dcfce7',
};

// Dark-mode background seed. antd's darkAlgorithm derives its whole background/
// border ramp (colorBgContainer, colorBgElevated, colorBorder, etc.) from a
// single colorBgBase seed, which defaults to pure black (#000). Requested
// replacement: a dark navy instead, so every derived dark-mode surface comes
// out as a shade of this color rather than black.
export const CAP_DARK = {
  bgBase: '#0f1221',
  // Explicit override for elevated/card surfaces (Card, Table, Input, Modal,
  // Dropdown, ...) — the algorithm's own lighten-from-bgBase shade landed
  // noticeably more blue/purple than this, so it's pinned directly instead.
  surface: '#1a1f35',
};

/**
 * "AST Console Revamp" design tokens — transcribed from the full-app redesign
 * mockup (Claude artifact "AST Console Revamp", f31b8e97-877a-48c9-8323-
 * 7ad7328b66f7), which is this app's current authoritative visual reference
 * and supersedes the earlier BiFrost-sampled border/radius/focus values
 * below it overlapped with (colorPrimary/colorSecondary already matched
 * BiFrost exactly — both design references agree there — but border color,
 * the radius scale, and the focus treatment differ, and the mockup wins).
 * Applied as global antd tokens in buildAntdTheme.ts, in both light and dark
 * mode — unlike BiFrost, this mockup ships its own complete dark palette
 * rather than leaving dark mode to antd's derived algorithm.
 */
export const CAP_REVAMP = {
  primary: {base: '#47AF46', hover: '#3B9339', press: '#2F7530', subtle: '#EFFBEE'},
  secondary: {base: '#2466EA', subtle: '#EAF1FE'},
  accent: {base: '#F2861D', subtle: '#FEF3E7'},
  semantic: {
    success: {fg: '#2F7530', bg: '#EFFBEE'},
    warning: {fg: '#93600A', bg: '#FEF6E6'},
    error: {fg: '#dc2626', bg: '#FDECEC'},
    info: {fg: '#0A5C71', bg: '#E7F8FB'},
    neutral: {fg: '#414A57', bg: '#EEF1F4'},
  },
  bg: {canvas: '#F7F8FA', surface: '#FFFFFF', surface2: '#EEF1F4'},
  text: {primary: '#171B21', secondary: '#5B6675', tertiary: '#7C8898', onBrand: '#FFFFFF'},
  // `border.focus` is the secondary blue, not colorPrimary green — the mockup's
  // Input/Select focus ring is blue-toned (box-shadow uses secondary.subtle),
  // distinct from the green used for buttons/active-tab/brand elsewhere.
  border: {default: '#CBD2DC', subtle: '#E2E6EC', focus: '#2466EA'},
  radius: {sm: 4, md: 8, lg: 12, full: 999},
  shadow: {
    sm: '0 1px 2px rgba(23,27,33,.06)',
    md: '0 4px 8px -2px rgba(23,27,33,.10), 0 2px 4px -1px rgba(23,27,33,.06)',
    lg: '0 16px 32px -6px rgba(23,27,33,.18), 0 4px 10px -2px rgba(23,27,33,.10)',
  },
  fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  dark: {
    primary: {base: '#64C65C', hover: '#8AD682', press: '#47AF46', subtle: '#1A421C'},
    secondary: {base: '#6B9EF5', subtle: '#0D2054'},
    accent: {base: '#F6932E', subtle: '#663408'},
    semantic: {
      success: {fg: '#8AD682', bg: '#1A421C'},
      warning: {fg: '#F6B94C', bg: '#4A3005'},
      error: {fg: '#f87171', bg: '#4A1113'},
      info: {fg: '#58C7DC', bg: '#052F38'},
      neutral: {fg: '#CBD2DC', bg: '#2A313C'},
    },
    // surface2 has no CAP_DARK equivalent to reuse (that block only seeds
    // bgBase/surface), so it's carried directly from the mockup.
    bg: {surface2: '#232A47'},
    // bg.canvas/surface intentionally match CAP_DARK.bgBase/surface above —
    // the mockup's dark palette and this file's pre-existing dark seed agree.
    text: {primary: '#F7F8FA', secondary: '#A9B3C1', tertiary: '#7C8898', onBrand: '#0F1221'},
    border: {default: '#2E3654', subtle: '#232A47', focus: '#6B9EF5'},
  },
};

/**
 * ---------------------------------------------------------------------------
 * Full Cap UI Library palette (Foundations / Color tokens / Palette).
 * Transcribed verbatim from the Storybook reference, using its own "TS name"
 * per swatch — flat constants, not nested, to match the source exactly. This
 * is the complete raw catalog; the curated groups above (CAP_PRIMARY,
 * CAP_SECONDARY, CAP_SEMANTIC, CAP_GREY, CAP_NEUTRAL, CAP_FONT_COLOR) are the
 * subset already wired into buildAntdTheme.ts and can stay as-is — their
 * values match entries below (e.g. CAP_GREY.G01 === CAP_G01). Pull from here
 * for anything not already covered by those.
 * ---------------------------------------------------------------------------
 */

// Brand and accent
export const CAP_BLUE = '#23cccc';
export const CAP_BLUE01 = '#2466eb';
export const CAP_BLUE02 = '#1d61ee';
export const CAP_GREEN01 = '#6bb56b';
export const CAP_GREEN02 = '#ecf7ec';
export const CAP_ICON = '#7a869a';
export const CAP_ORANGE = '#f87d23';
export const CAP_ORANGE01 = '#ffe5d2';
export const CAP_ORANGE02 = '#fa7d02';
export const CAP_PALE_GREY = '#e9f0fe';
export const CAP_PINK = '#e51fa3';
export const CAP_PURPLE = '#8517e5';
export const CAP_PURPLE01 = '#6563ff';
export const CAP_PURPLE02 = '#a451ff';
export const CAP_PURPLE03 = '#f2e7fe';
export const CAP_PURPLE04 = '#d4e1fc';
export const CAP_RED = '#ea213a';
export const CAP_RED01 = '#e51fa3';
export const CAP_RED02 = '#f5222d';
export const CAP_RED03 = '#ed1b34';
export const CAP_YELLOW = '#fec52e';
export const CAP_YELLOW01 = '#e8bc25';
export const CAP_YELLOW02 = '#f9d438';

// Neutrals
export const CAP_BLACK = '#000000';
export const CAP_BLACK_ALPHA01 = '#00000022';
export const CAP_BLACK_ALPHA08 = 'rgba(0, 0, 0, 0.08)';
export const CAP_WHITE = '#ffffff';
export const CAP_WHITE_OVERLAY = 'rgba(255, 255, 255, 0.8)';

// Grey scale — CAP_G01-10 are the same values as CAP_GREY.G01-10 above,
// under the source's own flat naming; CAP_G11-22 extend beyond that subset.
export const CAP_G01 = '#091e42';
export const CAP_G02 = '#253858';
export const CAP_G03 = '#42526e';
export const CAP_G04 = '#5e6c84';
export const CAP_G05 = '#97a0af';
export const CAP_G06 = '#b3bac5';
export const CAP_G07 = '#dfe2e7';
export const CAP_G08 = '#ebecf0';
export const CAP_G09 = '#f4f5f7';
export const CAP_G10 = '#fafbfc';
export const CAP_G11 = '#7a869a';
export const CAP_G12 = '#e8e8e8';
export const CAP_G13 = '#ecece7';
export const CAP_G14 = '#e9f0fd';
export const CAP_G15 = '#efefef';
export const CAP_G16 = '#2a2a2a';
export const CAP_G17 = '#5d6b85';
export const CAP_G18 = '#dcdee2';
export const CAP_G19 = '#8a9ab2';
export const CAP_G20 = '#c2c2c2';
export const CAP_G21 = '#fafafa';
export const CAP_G22 = '#f3f3f3';

// Semantic — same values as CAP_SEMANTIC above, under the source's own flat naming.
export const COLOR_ERROR = '#dc2626';
export const COLOR_INFO = '#1890ff';
export const COLOR_SUCCESS = '#52c41a';
export const COLOR_WARNING = '#faad14';

// CAP_COLOR — extended semantic/illustrative palette (chart series, status chips, etc.)
export const CAP_COLOR_01 = '#dbefda';
export const CAP_COLOR_02 = '#fee5d3';
export const CAP_COLOR_03 = '#e83135';
export const CAP_COLOR_04 = '#fff4d6';
export const CAP_COLOR_05 = '#fbd3d8';
export const CAP_COLOR_06 = '#42B040';
export const CAP_COLOR_07 = '#E9F0FC';
export const CAP_COLOR_08 = '#faf9f4';
export const CAP_COLOR_09 = '#7765d5';
export const CAP_COLOR_10 = '#f79600';
export const CAP_COLOR_11 = '#2388de';
export const CAP_COLOR_12 = '#efba00';
export const CAP_COLOR_13 = '#bcd0fa';
export const CAP_COLOR_14 = '#9BC79A';
export const CAP_COLOR_15 = '#F6BA8E';
export const CAP_COLOR_16 = '#d9d9d9';
export const CAP_COLOR_17 = '#1d61ee';
export const CAP_COLOR_18 = '#d0d7ff';
export const CAP_COLOR_19 = '#44ddff';
export const CAP_COLOR_20 = '#4a67ff';
export const CAP_COLOR_21 = '#ace0ff';
export const CAP_COLOR_22 = '#DBB7F9';
export const CAP_COLOR_23 = '#F8BAE4';
export const CAP_COLOR_24 = '#BCD0FA';
export const CAP_COLOR_25 = '#ACE1F1';
export const CAP_COLOR_26 = '#FFE9AD';
export const CAP_COLOR_27 = '#F4F7FE';

// Background
export const BG_01 = '#faf9f4';
export const BG_02 = '#fefcf0';
export const BG_03 = '#ebf8ff';
export const BG_04 = '#1d5b96';
export const BG_05 = '#85b940';
export const BG_06 = '#d0011b';
export const BG_07 = '#f9f9fa';
export const BG_08 = '#ecf7ec';

// Font colors — same values as CAP_FONT_COLOR above (which also documents this
// mapping in its own comments), plus FONT_COLOR_04 which that curated group omits.
export const FONT_COLOR_01 = '#091e42';
export const FONT_COLOR_02 = '#5e6c84';
export const FONT_COLOR_03 = '#97a0af';
export const FONT_COLOR_04 = '#b3bac5';
export const FONT_COLOR_05 = '#2466ea';
export const FONT_COLOR_06 = '#ffffff';

// User initials (avatar) palette — pick by e.g. hashing the user's name/id
// modulo the array length so each person gets a stable, distinct color.
export const USER_INITIALS_COLOR_CODES = [
  '#5F6C82',
  '#7A8698',
  '#AC5822',
  '#DD712D',
  '#B1892D',
  '#357935',
  '#449C44',
  '#1E4C41',
  '#228F8E',
  '#284AB0',
  '#3360E1',
  '#5C1C9D',
  '#7726CB',
  '#74121F',
  '#A21A2D',
  '#49260E',
  '#7B3F17',
  '#0254B8',
  '#1A79CF',
  '#9E1D71',
  '#CC2692',
];
